import classNames from "classnames";
import PropTypes from "prop-types";
import { useRef, useState } from "react";

import AuthTooltip from "../AuthTooltip";
import CardShell, { BackToSignIn } from "./card_shell";
import { PasswordField, TextField } from "./fields";
import OAuthButtons from "./oauth_buttons";
import { FlashBanner } from "./status_messages";

const SignInPage = ({
  flash,
  paths,
  authenticityToken,
  initialEmail,
  googleTooltip,
  microsoftTooltip,
  signInTitle = "Sign in to your account",
  magicLinkHelperText = "Enter your email to get a sign-in link.",
  passwordTooltipTitle = "Email and password",
  passwordTooltipBody = "Works with any email address. You'll still get bulk emails, drip campaigns, texts, and every core feature — but direct 1-on-1 emails and calendar invites from the contact profile won't send from your account until you also connect Google or Microsoft.",
  magicLinkTooltipBody = "No password needed. We'll email you a secure sign-in link valid for 24 hours — click it to sign in instantly.",
}) => {
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");
  const [showMagicLinkHelper, setShowMagicLinkHelper] = useState(false);
  const [magicLinkSubmitting, setMagicLinkSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const magicLinkEmail = email.trim().toLowerCase();

  if (flash?.magic_link_sent_to) {
    return (
      <CardShell size="wide">
        <div className="text-center">
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-confirmation-copy">
            We&apos;ve sent you a sign in link to {flash.magic_link_sent_to}.
          </p>
          <p className="auth-confirmation-copy">
            Click the link to log in. The link expires in 24 hours.
          </p>
          <BackToSignIn path={paths.signIn} />
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <h1 className="auth-title text-center">{signInTitle}</h1>

      <OAuthButtons
        authenticityToken={authenticityToken}
        googlePath={paths.googleOauth}
        microsoftPath={paths.microsoftOauth}
        googleTooltip={googleTooltip}
        microsoftTooltip={microsoftTooltip}
      />

      <div
        className="d-flex align-items-center text-muted small my-3"
        aria-hidden="true"
      >
        <span className="border-top flex-grow-1" />
        <span className="px-3">or</span>
        <span className="border-top flex-grow-1" />
      </div>

      <form action={paths.signIn} method="post">
        <input
          type="hidden"
          name="authenticity_token"
          value={authenticityToken}
        />

        <TextField
          id="user_email"
          name="user[email]"
          label="Email address"
          placeholder="Email address"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          inputRef={emailInputRef}
        />

        <div
          id="magic-link-helper"
          className={classNames("text-muted mt-1", {
            "d-none": !showMagicLinkHelper,
          })}
        >
          {magicLinkHelperText}
        </div>

        <PasswordField
          id="user_password"
          name="user[password]"
          label="Password"
          placeholder="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required={false}
          tooltipTitle={passwordTooltipTitle}
          tooltipBody={passwordTooltipBody}
        />

        <button
          type="submit"
          className="btn btn-lg btn-block auth-primary-button mt12"
        >
          Sign In
        </button>
      </form>

      <div className="mt15">
        <FlashBanner flash={flash} />
      </div>

      <div className="d-flex flex-column align-items-center justify-content-center text-center mt15">
        <a href={paths.passwordRequest} className="auth-secondary-link mb10">
          Set or reset your password
        </a>

        <form
          action={paths.magicLink}
          method="post"
          className="d-inline-flex align-items-center justify-content-center"
          onSubmit={(event) => {
            if (!magicLinkEmail) {
              event.preventDefault();
              setShowMagicLinkHelper(true);
              emailInputRef.current?.focus();
              return;
            }

            setMagicLinkSubmitting(true);
          }}
        >
          <input
            type="hidden"
            name="authenticity_token"
            value={authenticityToken}
          />
          <input type="hidden" name="email" value={magicLinkEmail} />
          <button
            type="submit"
            className="auth-secondary-link auth-link-button d-inline-flex align-items-center"
            disabled={magicLinkSubmitting}
          >
            <i
              className={classNames("far mr5", {
                "fa-spinner fa-spin": magicLinkSubmitting,
                "d-none": !magicLinkSubmitting,
              })}
              data-submit-spinner
              aria-hidden="true"
            />
            <span data-submit-label>Email me a link</span>
          </button>
          <span className="d-inline-flex align-items-center">
            <AuthTooltip title="Email me a link" body={magicLinkTooltipBody} />
          </span>
        </form>
      </div>
    </CardShell>
  );
};

SignInPage.propTypes = {
  flash: PropTypes.object,
  paths: PropTypes.shape({
    signIn: PropTypes.string.isRequired,
    passwordRequest: PropTypes.string.isRequired,
    magicLink: PropTypes.string.isRequired,
    googleOauth: PropTypes.string.isRequired,
    microsoftOauth: PropTypes.string.isRequired,
  }).isRequired,
  authenticityToken: PropTypes.string.isRequired,
  initialEmail: PropTypes.string,
  googleTooltip: PropTypes.string.isRequired,
  microsoftTooltip: PropTypes.string.isRequired,
  signInTitle: PropTypes.string,
  magicLinkHelperText: PropTypes.string,
  passwordTooltipTitle: PropTypes.string,
  passwordTooltipBody: PropTypes.string,
  magicLinkTooltipBody: PropTypes.string,
};

export default SignInPage;
